import { Badge } from '@/shared/components/Badge'
import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { Save, Upload, FileText, PencilLine, CheckCircle, XCircle, ArrowLeft, MessageSquare, Eye, PlusCircle } from 'lucide-react'
import { apiService } from '@/services/api'
import { Notification, useNotification } from '@/shared/components/Notification'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import { TruncatedText } from '@/shared/components/TruncatedText'
import { MEXICAN_STATES, US_STATES, US_CITIES, SAT_FISCAL_REGIMES, SAT_CFDI_USAGE, MEXICAN_BANKS, SERVICE_TYPES } from '@/constants/supplierConstants'
import { formatDate } from '@/shared/lib/dateUtils'
import { config } from '@/config/env'
import VigenciaBadge from '@/features/proveedores/components/VigenciaBadge'
import type { EstadoVigencia } from '@/features/proveedores/api/supplierProfile.service'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import AccesoDelProveedor from '@/features/proveedores/components/AccesoDelProveedor'
import { idSaiComoTexto, idSaiParaEdicion, validarIdSai } from '@/features/proveedores/lib/idSai'

interface AdminSupplierFormProps {
  onBack: () => void
  onSuccess?: () => void
  supplierId: string
}

type FormStep = 1 | 2 | 3 | 4

interface DocumentType {
  id: string
  code: string
  name: string
  description?: string
  isRequired: boolean
  personType?: string
  validityMonths?: number
  maxFileSizeMb: number
  allowedMimeTypes: string[]
}

interface SupplierDocument {
  id: string
  version: number
  fileName: string
  fileUrl: string
  status: 'pending_review' | 'approved' | 'observed' | 'rejected'
  uploadedAt: string
  reviewedAt?: string
  reviewComment?: string
  validUntil?: string | null
  estadoVigencia?: EstadoVigencia
}

interface DocumentComment {
  id: string
  documentId: string
  adminUserId: string
  comment: string
  createdAt: string
  adminUser: {
    id: string
    firstName: string
    lastName: string
  }
}

interface DocumentRequirement {
  documentType: DocumentType
  latestDocument: SupplierDocument | null
}

type ReviewStatus = 'approved' | 'observed' | 'rejected'



const AdminSupplierForm = ({ onBack, onSuccess, supplierId }: AdminSupplierFormProps) => {
  const [currentStep, setCurrentStep] = useState<FormStep>(1)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [documents, setDocuments] = useState<DocumentRequirement[]>([])
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  /**
   * SP-214. El tipo con el que se cargó el proveedor, para saber si cambió, y la
   * confirmación pendiente. Cambiar el tipo altera los documentos que se le
   * exigen en el expediente, así que no se guarda sin preguntar.
   */
  const [tipoOriginal, setTipoOriginal] = useState('')
  const [confirmacionDeTipo, setConfirmacionDeTipo] = useState<
    ((sigue: boolean) => void) | null
  >(null)

  // Estados para comentarios en documentos
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [commentingDocId, setCommentingDocId] = useState<string | null>(null)
  const [commentText, setCommentText] = useState('')
  const [isSavingComment, setIsSavingComment] = useState(false)
  const [commentsHistory, setCommentsHistory] = useState<DocumentComment[]>([])
  const [isLoadingComments, setIsLoadingComments] = useState(false)
  const [reviewingDocument, setReviewingDocument] = useState<{
    documentId: string
    documentName: string
    status: ReviewStatus
  } | null>(null)
  const [reviewComment, setReviewComment] = useState('')
  // Fecha de vencimiento dentro del modal de dictamen: se parte de la que
  // capturó el proveedor y Contraloría la confirma o la corrige.
  const [reviewValidUntil, setReviewValidUntil] = useState('')
  const [isSubmittingReview, setIsSubmittingReview] = useState(false)

  const { notification, showSuccess, showError, hideNotification } = useNotification()

  const [availableColonias, setAvailableColonias] = useState<string[]>([])

  const {
    register,
    control,
    watch,
    formState: { errors },
    setValue,
    reset,
    trigger
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      razonSocial: '',
      nombreComercial: '',
      tipoServicio: '',
      supplierType: '',
      contactoPrincipal: '',
      telefono: '',
      correoVentas: '',
      correoCuentas: '',
      diasCredito: '',
      saiSupplierId: '',
      rfc: '',
      tipoProveedor: '',
      regimenFiscal: '',
      usoCFDI: '',
      correoFiscal: '',
      telefonoFiscal: '',
      pais: '',
      estado: '',
      ciudad: '',
      municipio: '',
      calle: '',
      numero: '',
      colonia: '',
      cp: '',
      clabe: '',
      banco: '',
      nombreCuenta: '',
      numeroCuenta: '',
      moneda: 'MXN'
    }
  })

  const formData = watch()
  const normalizeTenDigitPhone = (value?: string | null): string => {
    const digits = (value || '').replace(/\D/g, '')
    return digits.length >= 10 ? digits.slice(0, 10) : digits
  }

  // Efecto para autocompletar dirección con CP
  const watchedCp = watch('cp')
  const watchedPais = watch('pais')

  useEffect(() => {
    const fetchPostalCodeData = async () => {
      // Solo ejecutar si el país es México y el CP tiene 5 dígitos numéricos
      if (watchedPais === 'MX' && watchedCp && watchedCp.length === 5 && /^\d+$/.test(watchedCp)) {
        try {
          const response = await fetch(`${config.apiBaseUrl}/catalogos/postal-codes/${watchedCp}`)

          if (response.ok) {
            const data = await response.json()

            // Autocompletar municipio
            if (data.municipio) {
              setValue('municipio', data.municipio, { shouldValidate: true })
            }

            // Autocompletar estado
            if (data.estado) {
              // Buscar el código del estado basado en el nombre
              const stateObj = MEXICAN_STATES.find(s =>
                s.name.toLowerCase() === data.estado.toLowerCase() ||
                s.name === data.estado
              )
              if (stateObj) {
                setValue('estado', stateObj.code, { shouldValidate: true })
              }
            }

            // Manejar colonias
            if (data.colonias && Array.isArray(data.colonias) && data.colonias.length > 0) {
              setAvailableColonias(data.colonias)

              // Si solo hay una colonia, seleccionarla automáticamente
              if (data.colonias.length === 1) {
                setValue('colonia', data.colonias[0], { shouldValidate: true })
              } else {
                // Si hay varias, verificar si el valor actual es válido
                const currentColonia = watch('colonia')
                if (currentColonia && !data.colonias.includes(currentColonia)) {
                  setValue('colonia', '', { shouldValidate: true })
                }
              }
            } else {
              setAvailableColonias([])
            }
          }
        } catch (error) {
          console.error('Error al obtener datos del código postal:', error)
          setAvailableColonias([])
        }
      } else {
        // En caso de que el usuario cambie el país o borre el CP, resetear colonias
        if (watchedPais !== 'MX' || !watchedCp || watchedCp.length < 5) {
          setAvailableColonias([])
        }
      }
    }

    fetchPostalCodeData()
  }, [watchedCp, watchedPais, setValue])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge color="verde">Aprobado</Badge>
        )
      case 'observed':
        return (
          <Badge color="naranja">Observado</Badge>
        )
      case 'rejected':
        return (
          <Badge color="rojo">Rechazado</Badge>
        )
      case 'pending_review':
        return (
          <Badge color="amarillo">Pendiente</Badge>
        )
      default:
        return <span className="text-gray-500 text-sm">N/A</span>
    }
  }

  const loadDocumentTypes = async () => {
    try {
      const profile = await apiService.request<any>(`/admin/suppliers/${supplierId}/profile`)
      const personType = profile.general?.personType
      if (personType) {
        const allDocTypes = await apiService.request<DocumentType[]>(
          `/admin/suppliers/document-types?personType=${personType}`
        )
        const uploadedMap = new Map<string, SupplierDocument | null>(
          (profile.documents || []).map((dr: DocumentRequirement) => [dr.documentType.id, dr.latestDocument])
        )
        setDocuments(allDocTypes.map(dt => ({ documentType: dt, latestDocument: uploadedMap.get(dt.id) ?? null })))
      } else {
        const allDocTypes = await apiService.request<DocumentType[]>('/admin/suppliers/document-types/all')
        const uploadedMap = new Map<string, SupplierDocument | null>(
          (profile.documents || []).map((dr: DocumentRequirement) => [dr.documentType.id, dr.latestDocument])
        )
        setDocuments(allDocTypes.map(dt => ({ documentType: dt, latestDocument: uploadedMap.get(dt.id) ?? null })))
      }
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar documentos:', error)
    }
  }

  const canSendToReview = (): boolean => {
    const requiredDocs = documents.filter(doc => doc.documentType.isRequired)
    return requiredDocs.every(doc =>
      doc.latestDocument &&
      doc.latestDocument.status === 'approved'
    )
  }

  const openReviewModal = (
    documentId: string,
    documentName: string,
    status: ReviewStatus,
    validUntil?: string | null,
  ) => {
    setReviewingDocument({ documentId, documentName, status })
    setReviewComment('')
    setReviewValidUntil((validUntil ?? '').slice(0, 10))
  }

  const closeReviewModal = () => {
    setReviewingDocument(null)
    setReviewComment('')
    setReviewValidUntil('')
  }

  const submitDocumentReview = async () => {
    if (!reviewingDocument) return

    const requiresComment =
      reviewingDocument.status === 'observed' ||
      reviewingDocument.status === 'rejected'

    if (requiresComment && !reviewComment.trim()) {
      showError('Comentario requerido', 'Debes capturar un comentario para observar o rechazar el documento.')
      return
    }

    try {
      setIsSubmittingReview(true)

      await apiService.request(
        `/internal/suppliers/${supplierId}/documents/${reviewingDocument.documentId}/review`,
        {
          method: 'POST',
          body: JSON.stringify({
            status: reviewingDocument.status,
            reviewComment: reviewComment.trim() || undefined,
            // Vacía va como null: es la forma de borrar una fecha mal capturada.
            validUntil: reviewValidUntil || null,
          })
        }
      )

      showSuccess('Documento dictaminado', `El documento quedó como ${reviewingDocument.status === 'approved' ? 'Aprobado' : reviewingDocument.status === 'observed' ? 'Observado' : 'Rechazado'}.`)
      closeReviewModal()
      await loadDocumentTypes()
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al dictaminar documento:', error)
      showError('Error', error.message || 'No se pudo actualizar el estatus del documento')
    } finally {
      setIsSubmittingReview(false)
    }
  }

  const handleSendToReview = async () => {
    try {
      setIsSaving(true)
      setShowConfirmModal(false)

      await apiService.request(`/admin/suppliers/${supplierId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'sent_to_review',
          notes: 'Expediente completado y enviado a revisión por el administrador'
        })
      })

      showSuccess('¡Éxito!', 'Proveedor enviado a revisión correctamente')
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
        } else {
          onBack()
        }
      }, 1500)
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al enviar a revisión:', error)
      showError('Error', error.message || 'No se pudo enviar a revisión')
    } finally {
      setIsSaving(false)
    }
  }

  const handleFileUpload = async (documentTypeId: string, file: File) => {
    try {
      setUploadingDocId(documentTypeId)

      const formData = new FormData()
      formData.append('documentTypeId', documentTypeId)
      formData.append('file', file)

      const token = localStorage.getItem('access_token')
      const response = await fetch(`${config.apiBaseUrl}/admin/suppliers/${supplierId}/documents`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
          // NO incluir Content-Type - el navegador lo establece automáticamente
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Error al subir el documento')
      }

      showSuccess('¡Éxito!', 'Documento subido correctamente')
      await loadDocumentTypes()
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al subir documento:', error)
      showError('Error', error.message || 'No se pudo subir el documento')
    } finally {
      setUploadingDocId(null)
    }
  }

  const handleFileInputChange = (documentTypeId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileUpload(documentTypeId, file)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      switch (currentStep) {
        case 1: {
          // Cancelar el cambio de tipo no guarda nada: sin este corte saldría
          // el aviso de guardado igual.
          const guardado = await saveStep1()
          if (!guardado) return
          break
        }
        case 2:
          await saveStep2()
          break
        case 3:
          await saveStep3()
          break
        case 4:
          showSuccess('¡Éxito!', 'Documentos guardados correctamente')
          break
      }
      showSuccess('¡Éxito!', 'Datos guardados correctamente')
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al guardar:', error)
      showError('Error al guardar', error.message || 'No se pudieron guardar los datos')
    } finally {
      setIsSaving(false)
    }
  }

  // Guardar Paso 1: Datos Generales
  const saveStep1 = async (): Promise<boolean> => {
    const tipoNuevo = formData.supplierType || ''
    if (tipoOriginal && tipoNuevo && tipoNuevo !== tipoOriginal) {
      const sigue = await new Promise<boolean>((resolver) =>
        setConfirmacionDeTipo(() => resolver),
      )
      if (!sigue) {
        // Cancelar deja el tipo como estaba: quedarse con el nuevo sin guardar
        // haría creer que el cambio se aplicó.
        setValue('supplierType', tipoOriginal)
        return false
      }
    }

    await apiService.request(`/admin/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify({
        legalName: formData.razonSocial,
        commercialName: formData.nombreComercial,
        serviceTypeId: formData.tipoServicio,
        mainContactName: formData.contactoPrincipal,
        mainContactPhone: formData.telefono,
        // Vacío manda null: así se borra el ID ya guardado.
        saiSupplierId: idSaiParaEdicion(formData.saiSupplierId),
        // Se omite vacío, igual que los días de crédito.
        ...(formData.supplierType && { supplierType: formData.supplierType }),
        // Se omite vacío: el back valida 1-60 y no acepta null.
        ...(formData.diasCredito.trim() !== '' && {
          creditDays: Number(formData.diasCredito),
        })
      })
    })

    // Ya quedó guardado: es el nuevo punto de comparación, para no volver a
    // preguntar por un cambio que ya se aplicó.
    setTipoOriginal(tipoNuevo)
    return true
  }

  // Guardar Paso 2: Información Fiscal y Dirección
  const saveStep2 = async () => {
    // 1. Guardar Información Fiscal
    await apiService.request(`/admin/suppliers/${supplierId}/fiscal`, {
      method: 'PUT',
      body: JSON.stringify({
        taxRegime: formData.regimenFiscal,
        cfdiUse: formData.usoCFDI,
        taxEmail: formData.correoFiscal,
        taxPhone: formData.telefonoFiscal,
        rfc: formData.rfc
      })
    })

    // 2. Guardar Dirección (en endpoint General)
    await apiService.request(`/admin/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify({
        country: formData.pais,
        state: formData.estado,
        city: formData.municipio, // Map municipio to city/municipality
        municipality: formData.municipio,
        postalCode: formData.cp,
        street: formData.calle,
        externalNumber: formData.numero,
        suburb: formData.colonia
      })
    })
  }

  // Guardar Paso 3: Información Bancaria
  const saveStep3 = async () => {
    await apiService.request(`/admin/suppliers/${supplierId}/bank-account`, {
      method: 'POST',
      body: JSON.stringify({
        bankName: formData.banco,
        clabe: formData.clabe,
        accountNumber: formData.numeroCuenta || undefined,
        currency: formData.moneda || 'MXN',
        accountHolderName: formData.nombreCuenta
      })
    })
  }


  // Manejo de comentarios
  const fetchDocumentComments = async (docId: string) => {
    try {
      setIsLoadingComments(true)
      const token = localStorage.getItem('access_token')
      const response = await fetch(`${config.apiBaseUrl}/admin/suppliers/documents/${docId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCommentsHistory(data || [])
      } else {
        setCommentsHistory([])
      }
    } catch (error) {
      console.error('Error al cargar comentarios:', error)
      setCommentsHistory([])
    } finally {
      setIsLoadingComments(false)
    }
  }

  const handleOpenComment = (docId: string, _currentComment?: string) => {
    setCommentingDocId(docId)
    setCommentText('') // Limpiar el texto para nuevo comentario
    setIsCommentModalOpen(true)
    fetchDocumentComments(docId)
  }

  const handleCloseComment = () => {
    setIsCommentModalOpen(false)
    setCommentingDocId(null)
    setCommentText('')
  }

  const handleSaveComment = async () => {
    if (!commentingDocId) return

    try {
      setIsSavingComment(true)

      const token = localStorage.getItem('access_token')
      const response = await fetch(`${config.apiBaseUrl}/admin/suppliers/documents/${commentingDocId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment: commentText
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Error al guardar el comentario')
      }

      showSuccess('¡Éxito!', 'Comentario guardado correctamente')
      setCommentText('') // Limpiar campo
      await fetchDocumentComments(commentingDocId) // Recargar comentarios
      await loadDocumentTypes() // Recargar documentos para actualizar estado si es necesario
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al guardar comentario:', error)
      showError('Error', error.message || 'No se pudo guardar el comentario')
    } finally {
      setIsSavingComment(false)
    }
  }


  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: string[] = []
    switch (step) {
      case 1:
        fieldsToValidate = ['razonSocial', 'tipoServicio', 'supplierType', 'contactoPrincipal']
        break
      case 2:
        fieldsToValidate = ['rfc', 'regimenFiscal', 'usoCFDI', 'correoFiscal', 'telefonoFiscal', 'pais', 'cp', 'estado', 'municipio', 'colonia', 'calle', 'numero']
        break
      case 3:
        fieldsToValidate = ['clabe', 'banco', 'nombreCuenta']
        break
      case 4:
        return canSendToReview()
    }

    if (fieldsToValidate.length > 0) {
      return await trigger(fieldsToValidate as any)
    }
    return true
  }

  const handleNext = async () => {
    // Validar paso actual antes de avanzar
    const isValid = await validateStep(currentStep)
    if (!isValid) {
      showError('Falta información', 'Por favor completa todos los campos requeridos de la pestaña actual.')
      return
    }

    if (currentStep === 4) {
      // Validar documentos requeridos antes de mostrar modal
      if (!canSendToReview()) {
        showError('Dictamen incompleto', 'Debes aprobar todos los documentos obligatorios antes de finalizar el expediente.')
        return
      }
      // Mostrar modal de confirmación
      setShowConfirmModal(true)
      return
    }

    setIsSaving(true)
    try {
      switch (currentStep) {
        case 1: {
          const guardado = await saveStep1()
          if (!guardado) return
          break
        }
        case 2:
          await saveStep2()
          break
        case 3:
          await saveStep3()
          break
      }

      setCurrentStep((currentStep + 1) as FormStep)
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al guardar:', error)
      showError('Error al guardar', error.message || 'Por favor, verifica la información e intenta nuevamente')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as FormStep)
    }
  }

  const steps = [
    { number: 1, label: 'Datos generales' },
    { number: 2, label: 'Información fiscal' },
    { number: 3, label: 'Información bancaria' },
    { number: 4, label: 'Documentos requeridos' }
  ]

  // Cargar datos del proveedor al montar el componente
  useEffect(() => {
    const loadSupplierProfile = async () => {
      setIsLoading(true)
      try {
        const profile = await apiService.request<any>(`/admin/suppliers/${supplierId}/profile`)
        const contactoTelefono = normalizeTenDigitPhone(
          profile.general.mainContactPhone || profile.fiscal?.taxPhone || profile.general.taxPhone || ''
        )
        const fiscalTelefono = normalizeTenDigitPhone(
          profile.fiscal?.taxPhone || profile.general.taxPhone || profile.general.mainContactPhone || ''
        )

        setTipoOriginal(profile.general.supplierType || '')

        reset({
          razonSocial: profile.general.legalName || '',
          nombreComercial: profile.general.commercialName || '',
          tipoServicio: profile.general.serviceTypeId || '',
          supplierType: profile.general.supplierType || '',
          contactoPrincipal: profile.general.mainContactName || '',
          telefono: contactoTelefono,
          correoVentas: profile.general.salesEmail || '',
          correoCuentas: profile.general.accountsPayableEmail || '',
          diasCredito:
            profile.general.creditDays != null ? String(profile.general.creditDays) : '',
          saiSupplierId: idSaiComoTexto(profile.general.saiSupplierId),
          rfc: profile.general.rfc || '',
          tipoProveedor: profile.general.personType || '',
          regimenFiscal: profile.fiscal?.taxRegime || profile.general.taxRegime || '',
          usoCFDI: profile.fiscal?.cfdiUse || profile.general.cfdiUse || '',
          correoFiscal: profile.fiscal?.taxEmail || profile.general.taxEmail || '',
          telefonoFiscal: fiscalTelefono,
          pais: profile.general.country || '',
          estado: profile.general.state || '',
          ciudad: profile.general.city || '',
          municipio: profile.general.municipality || '',
          calle: profile.general.street || '',
          numero: profile.general.externalNumber || '',
          colonia: profile.general.suburb || '',
          cp: profile.general.postalCode || '',
          clabe: profile.primaryBankAccount?.clabe || '',
          banco: profile.primaryBankAccount?.bankName || '',
          nombreCuenta: profile.primaryBankAccount?.accountHolderName || '',
          numeroCuenta: profile.primaryBankAccount?.accountNumber || '',
          moneda: profile.primaryBankAccount?.currency || 'MXN'
        })

        const personType = profile.general?.personType
        if (personType) {
          const allDocTypes = await apiService.request<DocumentType[]>(
            `/admin/suppliers/document-types?personType=${personType}`
          )
          const uploadedMap = new Map<string, SupplierDocument | null>(
            (profile.documents || []).map((dr: DocumentRequirement) => [dr.documentType.id, dr.latestDocument])
          )
          setDocuments(allDocTypes.map(dt => ({ documentType: dt, latestDocument: uploadedMap.get(dt.id) ?? null })))
        } else {
          const allDocTypes = await apiService.request<DocumentType[]>('/admin/suppliers/document-types/all')
          const uploadedMap = new Map<string, SupplierDocument | null>(
            (profile.documents || []).map((dr: DocumentRequirement) => [dr.documentType.id, dr.latestDocument])
          )
          setDocuments(allDocTypes.map(dt => ({ documentType: dt, latestDocument: uploadedMap.get(dt.id) ?? null })))
        }
      } catch (errorCrudo: unknown) {
        const error = errorCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        console.error('Error al cargar proveedor:', error)
        showError('Error', 'No se pudo cargar la información del proveedor')
      } finally {
        setIsLoading(false)
      }
    }

    loadSupplierProfile()
  }, [supplierId])



  if (isLoading) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mb-4')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Completar expediente del proveedor</h1>
          <p className="text-sm text-gray-600 mt-1">Completa la información del proveedor</p>
        </div>

        {/* Steps Navigation */}
        <div className="flex gap-3 mb-8">
          {steps.map((step) => (
            <button
              key={step.number}
              onClick={() => setCurrentStep(step.number as FormStep)}
              className={`flex-1 px-6 py-3 rounded-lg text-sm font-medium transition-all ${currentStep === step.number
                ? 'text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                }`}
              style={currentStep === step.number ? { backgroundColor: '#315FB9' } : {}}
            >
              {step.number}. {step.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {steps[currentStep - 1].label}
          </h2>

          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Razón social <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('razonSocial', {
                      required: 'La razón social es requerida',
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="------"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.razonSocial
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('razonSocial')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('razonSocial') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.razonSocial ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.razonSocial && (
                  <p className="text-xs text-red-600 mt-1">{errors.razonSocial.message}</p>
                )}
                {!errors.razonSocial && watch('razonSocial') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre comercial
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('nombreComercial', {
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="Ingresa el nombre comercial"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.nombreComercial
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('nombreComercial')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('nombreComercial') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.nombreComercial ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.nombreComercial && (
                  <p className="text-xs text-red-600 mt-1">{errors.nombreComercial.message}</p>
                )}
                {!errors.nombreComercial && watch('nombreComercial') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ID interno (SAI)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  {...register('saiSupplierId', { validate: validarIdSai })}
                  placeholder="42"
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.saiSupplierId ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                />
                {errors.saiSupplierId ? (
                  <p className="text-xs text-red-600 mt-1">{errors.saiSupplierId.message as string}</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Opcional. Número del proveedor en SAI, de 1 a 99999. Vacío lo borra.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de servicio <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Controller name="tipoServicio" control={control} rules={{ required: 'El tipo de servicio es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="tipoServicio" name="tipoServicio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.tipoServicio)} placeholder="Seleccionar tipo de servicio" placeholderDeBusqueda="Buscar tipo de servicio…" opciones={SERVICE_TYPES.map((s) => ({ value: s.id, label: s.name }))} />
                  )} />
                </div>
                {errors.tipoServicio && (
                  <p className="text-xs text-red-600 mt-1">{errors.tipoServicio.message}</p>
                )}
                {!errors.tipoServicio && watch('tipoServicio') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              {/* SP-214: Tipo de proveedor (General / Servicios / Fletes). Distinto del
                  tipo de persona (personType) del paso fiscal. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de proveedor <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Controller name="supplierType" control={control} rules={{ required: 'El tipo de proveedor es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="supplierType" name="supplierType" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.supplierType)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar tipo de proveedor…" opciones={[{ value: 'general', label: 'General' }, { value: 'servicios', label: 'Servicios' }, { value: 'fletes', label: 'Fletes' }]} />
                  )} />
                </div>
                {errors.supplierType && (
                  <p className="text-xs text-red-600 mt-1">{errors.supplierType.message as string}</p>
                )}
                {!errors.supplierType && watch('supplierType') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contacto principal <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('contactoPrincipal', {
                      required: 'El contacto principal es requerido',
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="Nombre del contacto"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.contactoPrincipal
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('contactoPrincipal')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('contactoPrincipal') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.contactoPrincipal ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.contactoPrincipal && (
                  <p className="text-xs text-red-600 mt-1">{errors.contactoPrincipal.message}</p>
                )}
                {!errors.contactoPrincipal && watch('contactoPrincipal') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              {/* Condiciones de pago — sección propia para que el dato no se
                  pierda entre los campos de datos generales. */}
              <div className="md:col-span-2 mt-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Condiciones de pago</h3>
                <div className="max-w-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Días de crédito</label>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    step={1}
                    {...register('diasCredito', {
                      validate: (value: string) => {
                        if (!value || value.trim() === '') return true
                        if (!/^\d+$/.test(value.trim())) return 'Debe ser un número entero'
                        const n = Number(value)
                        if (n < 1 || n > 60) return 'Debe estar entre 1 y 60 días'
                        return true
                      },
                    })}
                    placeholder="30"
                    className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm border-gray-300"
                  />
                  {errors.diasCredito && (
                    <p className="text-xs text-red-600 mt-1">{errors.diasCredito.message as string}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Se usa para calcular la fecha de vencimiento de las facturas del proveedor.
                  </p>
                </div>
              </div>

            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-8">
              {/* Información Fiscal */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Información fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RFC <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('rfc', {
                          required: 'El RFC es requerido',
                          pattern: {
                            value: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/,
                            message: 'RFC inválido'
                          },
                          onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase()
                          }
                        })}
                        placeholder="AAAA000000000"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.rfc
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('rfc')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('rfc') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.rfc ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.rfc && (
                      <p className="text-xs text-red-600 mt-1">{errors.rfc.message}</p>
                    )}
                    {!errors.rfc && watch('rfc') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Régimen fiscal <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Controller name="regimenFiscal" control={control} rules={{ required: 'El régimen fiscal es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="regimenFiscal" name="regimenFiscal" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.regimenFiscal)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar régimen…" opciones={SAT_FISCAL_REGIMES.map((r) => ({ value: r.code, label: `${r.code} - ${r.name}` }))} />
                      )} />
                    </div>
                    {errors.regimenFiscal && (
                      <p className="text-xs text-red-600 mt-1">{errors.regimenFiscal.message}</p>
                    )}
                    {!errors.regimenFiscal && watch('regimenFiscal') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Uso de CFDI <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Controller name="usoCFDI" control={control} rules={{ required: 'El uso de CFDI es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="usoCFDI" name="usoCFDI" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.usoCFDI)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar uso de CFDI…" opciones={SAT_CFDI_USAGE.map((u) => ({ value: u.code, label: `${u.code} - ${u.name}` }))} />
                      )} />
                    </div>
                    {errors.usoCFDI && (
                      <p className="text-xs text-red-600 mt-1">{errors.usoCFDI.message}</p>
                    )}
                    {!errors.usoCFDI && watch('usoCFDI') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correo fiscal <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        {...register('correoFiscal', {
                          required: 'El correo fiscal es requerido',
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: 'Formato de correo inválido'
                          }
                        })}
                        placeholder="Ej. empresa@example.com"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.correoFiscal
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('correoFiscal')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('correoFiscal') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.correoFiscal ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.correoFiscal && (
                      <p className="text-xs text-red-600 mt-1">{errors.correoFiscal.message}</p>
                    )}
                    {!errors.correoFiscal && watch('correoFiscal') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('telefonoFiscal', {
                          required: 'El teléfono es requerido',
                          pattern: {
                            value: /^\d{10}$/,
                            message: 'Debe contener exactamente 10 dígitos'
                          }
                        })}
                        placeholder="5512345678"
                        maxLength={10}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.telefonoFiscal
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('telefonoFiscal')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('telefonoFiscal') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.telefonoFiscal ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.telefonoFiscal && (
                      <p className="text-xs text-red-600 mt-1">{errors.telefonoFiscal.message}</p>
                    )}
                    {!errors.telefonoFiscal && watch('telefonoFiscal') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {watch('telefonoFiscal')?.length || 0}/10
                    </p>
                  </div>
                </div>
              </div>

              {/* Domicilio fiscal */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Domicilio fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      País <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Controller name="pais" control={control} rules={{ required: 'El país es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="pais" name="pais" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('estado', ''); setValue('municipio', ''); setValue('colonia', ''); setValue('cp', ''); setAvailableColonias([]) }} error={Boolean(errors.pais)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar país…" opciones={[{ value: 'MX', label: 'México' }, { value: 'US', label: 'Estados Unidos' }]} />
                      )} />
                    </div>
                    {errors.pais && (
                      <p className="text-xs text-red-600 mt-1">{errors.pais.message}</p>
                    )}
                    {!errors.pais && watch('pais') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CP <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('cp', {
                          required: 'El código postal es requerido',
                          pattern: {
                            value: /^\d{5}$/,
                            message: 'Debe contener exactamente 5 dígitos'
                          }
                        })}
                        placeholder="12345"
                        maxLength={5}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.cp
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('cp')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('cp') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.cp ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.cp && (
                      <p className="text-xs text-red-600 mt-1">{errors.cp.message}</p>
                    )}
                    {!errors.cp && watch('cp') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {watch('cp')?.length || 0}/5
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Controller name="estado" control={control} rules={{ required: 'El estado es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="estado" name="estado" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('municipio', '') }} error={Boolean(errors.estado)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar estado…" opciones={(watch('pais') === 'MX' ? MEXICAN_STATES : watch('pais') === 'US' ? US_STATES : []).map((e) => ({ value: e.code, label: e.name }))} />
                      )} />
                    </div>
                    {errors.estado && (
                      <p className="text-xs text-red-600 mt-1">{errors.estado.message}</p>
                    )}
                    {!errors.estado && watch('estado') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Municipio <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      {watch('pais') === 'US' && watch('estado') && US_CITIES[watch('estado')] ? (
                        <div className="relative">
                          <Controller name="municipio" control={control} rules={{ required: 'El municipio es requerido' }} render={({ field }) => (
                            <SelectBuscable variante="formulario" id="municipio" name="municipio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.municipio)} placeholder="Seleccionar municipio/ciudad" placeholderDeBusqueda="Buscar municipio…" opciones={US_CITIES[watch('estado')].map((c) => ({ value: c, label: c }))} />
                          )} />
                        </div>
                      ) : (
                        <input
                          type="text"
                          {...register('municipio', {
                            required: 'El municipio es requerido',
                            minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                          })}
                          placeholder="Ingresa el municipio"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.municipio
                            ? 'border-red-500 bg-red-50 focus:border-red-500'
                            : watch('municipio')
                              ? 'border-green-500 bg-green-50 focus:border-green-500'
                              : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                            }`}
                        />
                      )}

                      {watch('municipio') && (!watch('pais') || watch('pais') !== 'US' || !US_CITIES[watch('estado')] || US_CITIES[watch('estado')].length === 0) && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.municipio ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.municipio && (
                      <p className="text-xs text-red-600 mt-1">{errors.municipio.message}</p>
                    )}
                    {!errors.municipio && watch('municipio') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Colonia <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      {availableColonias.length > 0 ? (
                        <div className="relative">
                          <Controller name="colonia" control={control} rules={{ required: 'La colonia es requerida' }} render={({ field }) => (
                            <SelectBuscable variante="formulario" id="colonia" name="colonia" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.colonia)} placeholder="Seleccionar colonia" placeholderDeBusqueda="Buscar colonia…" opciones={availableColonias.map((c) => ({ value: c, label: c }))} />
                          )} />
                        </div>
                      ) : (
                        <input
                          type="text"
                          {...register('colonia', {
                            required: 'La colonia es requerida',
                            minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                          })}
                          placeholder="Ingresa la colonia"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.colonia
                            ? 'border-red-500 bg-red-50 focus:border-red-500'
                            : watch('colonia')
                              ? 'border-green-500 bg-green-50 focus:border-green-500'
                              : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                            }`}
                        />
                      )}
                      {watch('colonia') && availableColonias.length === 0 && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.colonia ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.colonia && (
                      <p className="text-xs text-red-600 mt-1">{errors.colonia.message}</p>
                    )}
                    {!errors.colonia && watch('colonia') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calle <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('calle', {
                          required: 'La calle es requerida',
                          minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                        })}
                        placeholder="Ingresa la calle"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.calle
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('calle')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('calle') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.calle ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.calle && (
                      <p className="text-xs text-red-600 mt-1">{errors.calle.message}</p>
                    )}
                    {!errors.calle && watch('calle') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('numero', {
                          required: 'El número es requerido'
                        })}
                        placeholder="Ingresa el número"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.numero
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : watch('numero')
                            ? 'border-green-500 bg-green-50 focus:border-green-500'
                            : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                          }`}
                      />
                      {watch('numero') && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {errors.numero ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {errors.numero && (
                      <p className="text-xs text-red-600 mt-1">{errors.numero.message}</p>
                    )}
                    {!errors.numero && watch('numero') && (
                      <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ paddingBottom: '6.25rem' }}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CLABE <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('clabe', {
                      required: 'La CLABE es requerida',
                      pattern: {
                        value: /^\d{18}$/,
                        message: 'Debe contener exactamente 18 dígitos'
                      }
                    })}
                    placeholder="123456789012345678"
                    maxLength={18}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.clabe
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('clabe')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('clabe') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.clabe ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.clabe && (
                  <p className="text-xs text-red-600 mt-1">{errors.clabe.message}</p>
                )}
                {!errors.clabe && watch('clabe') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {watch('clabe')?.length || 0}/18
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de cuenta
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('numeroCuenta')}
                    placeholder="Ej. 1234567890"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${watch('numeroCuenta') ? 'border-green-500 bg-green-50 focus:border-green-500' : 'border-gray-300 bg-gray-50 focus:border-blue-500'}`}
                  />
                  {watch('numeroCuenta') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banco <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Controller name="banco" control={control} rules={{ required: 'El banco es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="banco" name="banco" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.banco)} placeholder="Seleccionar banco" placeholderDeBusqueda="Buscar banco…" opciones={MEXICAN_BANKS.map((b) => ({ value: b.name, label: b.name }))} />
                  )} />
                </div>
                {errors.banco && (
                  <p className="text-xs text-red-600 mt-1">{errors.banco.message}</p>
                )}
                {!errors.banco && watch('banco') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de cuenta <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register('nombreCuenta', {
                      required: 'El nombre de cuenta es requerido',
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                    })}
                    placeholder="Nombre del titular de la cuenta"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${errors.nombreCuenta
                      ? 'border-red-500 bg-red-50 focus:border-red-500'
                      : watch('nombreCuenta')
                        ? 'border-green-500 bg-green-50 focus:border-green-500'
                        : 'border-gray-300 bg-gray-50 focus:border-blue-500'
                      }`}
                  />
                  {watch('nombreCuenta') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {errors.nombreCuenta ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {errors.nombreCuenta && (
                  <p className="text-xs text-red-600 mt-1">{errors.nombreCuenta.message}</p>
                )}
                {!errors.nombreCuenta && watch('nombreCuenta') && (
                  <p className="text-xs text-green-600 mt-1">✓ Válido</p>
                )}
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>Cargando documentos...</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Documento</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Archivo</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Fecha</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Vigencia</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Comentarios</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {documents.map((doc) => (
                        <tr key={doc.documentType.id} className="hover:bg-[#F4F7FB] transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <TruncatedText text={doc.documentType.name} maxWidthClass="max-w-[230px]" className="text-sm font-medium text-gray-900" />
                              {doc.documentType.description && (
                                <TruncatedText text={doc.documentType.description} maxWidthClass="max-w-[230px]" className="text-xs text-gray-500" />
                              )}
                            </div>
                          </td>

                          <td className="px-6 py-4 text-sm">
                            {doc.latestDocument ? (
                              <a
                                href={doc.latestDocument.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline"
                              >
                                <FileText className="w-4 h-4" />
                                <TruncatedText text={doc.latestDocument.fileName} maxWidthClass="max-w-[220px]" />
                              </a>
                            ) : (
                              <label className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline cursor-pointer">
                                {uploadingDocId === doc.documentType.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    Subiendo...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Subir archivo
                                  </>
                                )}
                                <input
                                  type="file"
                                  className="hidden"
                                  accept={doc.documentType.allowedMimeTypes.join(',')}
                                  onChange={handleFileInputChange(doc.documentType.id)}
                                  disabled={uploadingDocId === doc.documentType.id}
                                />
                              </label>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {doc.latestDocument ? formatDate(doc.latestDocument.uploadedAt) : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            {doc.latestDocument ? getStatusBadge(doc.latestDocument.status) : <span className="text-gray-500 text-sm">Sin subir</span>}
                          </td>
                          <td className="px-6 py-4">
                            <VigenciaBadge
                              estado={doc.latestDocument?.estadoVigencia}
                              validUntil={doc.latestDocument?.validUntil}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {doc.latestDocument ? (
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleOpenComment(doc.latestDocument!.id)}
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <PlusCircle className="w-4 h-4" />
                                  {doc.latestDocument.reviewComment ? 'Agregar' : 'Agregar comentario'}
                                </button>

                                {doc.latestDocument.reviewComment && (
                                  <button
                                    onClick={() => handleOpenComment(doc.latestDocument!.id)}
                                    className="inline-flex items-center gap-1 text-cyan-600 hover:text-cyan-800 text-sm font-medium"
                                  >
                                    <Eye className="w-4 h-4" />
                                    Ver
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Sin documento</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {doc.latestDocument ? (
                                <>
                                  <button
                                    onClick={() => openReviewModal(doc.latestDocument!.id, doc.documentType.name, 'approved', doc.latestDocument!.validUntil)}
                                    className={estiloDeBoton('exito')}
                                    title="Aprobar documento"
                                  >
                                    Aprobar
                                  </button>
                                  <button
                                    onClick={() => openReviewModal(doc.latestDocument!.id, doc.documentType.name, 'observed', doc.latestDocument!.validUntil)}
                                    className={estiloDeBoton('secundario')}
                                    title="Observar documento"
                                  >
                                    Observar
                                  </button>
                                  <button
                                    onClick={() => openReviewModal(doc.latestDocument!.id, doc.documentType.name, 'rejected', doc.latestDocument!.validUntil)}
                                    className={estiloDeBoton('peligro')}
                                    title="Rechazar documento"
                                  >
                                    Rechazar
                                  </button>
                                  <label className="p-1.5 rounded hover:bg-blue-50 text-blue-600 cursor-pointer">
                                    <PencilLine className="w-4 h-4" />
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept={doc.documentType.allowedMimeTypes.join(',')}
                                      onChange={handleFileInputChange(doc.documentType.id)}
                                      disabled={uploadingDocId === doc.documentType.id}
                                    />
                                  </label>
                                </>
                              ) : (
                                <button
                                  disabled
                                  className="p-1.5 rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                                >
                                  <PencilLine className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CC-03 (SP-251). Se aplica al momento, por eso va fuera del formulario. */}
        {currentStep === 1 && <AccesoDelProveedor supplierId={supplierId} />}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={estiloDeBoton('primario')}
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Guardando...' : 'Guardar'}</span>
          </button>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={isSaving}
                className={estiloDeBoton('secundario')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Anterior</span>
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={isSaving}
              className={estiloDeBoton('primario')}
            >
              <span>{isSaving ? 'Guardando...' : (currentStep === 4 ? 'Finalizar expediente' : 'Siguiente')}</span>
              {!isSaving && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Modal de Confirmación */}
        <ConfirmActionModal
          isOpen={showConfirmModal}
          title="Enviar Expediente a Revisión"
          message="¿Estás seguro de que deseas finalizar y enviar este expediente a revisión? El proveedor pasará al estado 'Enviado a Revisión'."
          confirmText="Enviar a Revisión"
          variante="primario"
          onConfirm={handleSendToReview}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={isSaving}
        />

        {/* SP-214. Cambiar el tipo redefine los documentos que se le exigen al
            proveedor, así que se confirma antes de guardar. */}
        <ConfirmActionModal
          isOpen={confirmacionDeTipo !== null}
          title="Cambiar el tipo de proveedor"
          message="Cambiar el tipo de proveedor modificará los documentos requeridos en su expediente. ¿Confirmas?"
          confirmText="Confirmar"
          onConfirm={() => {
            confirmacionDeTipo?.(true)
            setConfirmacionDeTipo(null)
          }}
          onCancel={() => {
            confirmacionDeTipo?.(false)
            setConfirmacionDeTipo(null)
          }}
        />

        {/* Modal de Dictamen de Documento */}
        {reviewingDocument && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeReviewModal}></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {reviewingDocument.status === 'approved' && 'Aprobar documento'}
                    {reviewingDocument.status === 'observed' && 'Observar documento'}
                    {reviewingDocument.status === 'rejected' && 'Rechazar documento'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Documento: <span className="font-medium text-gray-900">{reviewingDocument.documentName}</span>
                  </p>

                  {(reviewingDocument.status === 'observed' || reviewingDocument.status === 'rejected') && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Comentario <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={4}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                        placeholder={reviewingDocument.status === 'observed' ? 'Describe la observación para que el proveedor pueda corregir el documento...' : 'Describe el motivo del rechazo del documento...'}
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Contraloría confirma o corrige la fecha que capturó el proveedor. */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de vencimiento
                    </label>
                    <input
                      type="date"
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                      value={reviewValidUntil}
                      onChange={(e) => setReviewValidUntil(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      La capturó el proveedor al subir. Déjala vacía para borrarla.
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className={estiloDeBoton('primario', 'w-full sm:ml-3 sm:w-auto')}
                    onClick={submitDocumentReview}
                    disabled={
                      isSubmittingReview ||
                      ((reviewingDocument.status === 'observed' || reviewingDocument.status === 'rejected') && !reviewComment.trim())
                    }
                  >
                    {isSubmittingReview ? 'Guardando...' : 'Confirmar'}
                  </button>
                  <button
                    type="button"
                    className={estiloDeBoton('secundario', 'mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto')}
                    onClick={closeReviewModal}
                    disabled={isSubmittingReview}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Comentarios */}
        {isCommentModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={handleCloseComment}></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <MessageSquare className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Historial de Comentarios
                      </h3>

                      {/* Lista de Comentarios */}
                      <div className="mt-4 mb-4 max-h-60 overflow-y-auto bg-gray-50 rounded-md p-3">
                        {isLoadingComments ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : commentsHistory.length > 0 ? (
                          <div className="space-y-3">
                            {commentsHistory.map((comment) => (
                              <div key={comment.id} className="bg-white p-3 rounded shadow-sm border border-gray-100">
                                <div className="flex justify-between items-start mb-1">
                                  <span className="font-medium text-xs text-blue-800">
                                    {comment.adminUser?.firstName} {comment.adminUser?.lastName}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {new Date(comment.createdAt).toLocaleDateString('es-MX', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">{comment.comment}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4">No hay comentarios previos.</p>
                        )}
                      </div>

                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo Comentario</label>
                        <textarea
                          rows={3}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                          placeholder="Escribe un comentario..."
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className={estiloDeBoton('primario', 'w-full sm:ml-3 sm:w-auto')}
                    onClick={handleSaveComment}
                    disabled={isSavingComment || !commentText.trim()}
                  >
                    {isSavingComment ? 'Guardando...' : 'Guardar Comentario'}
                  </button>
                  <button
                    type="button"
                    className={estiloDeBoton('secundario', 'mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto')}
                    onClick={handleCloseComment}
                    disabled={isSavingComment}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default AdminSupplierForm
